"use client";

import { EventCard } from "@/components/event-card";
import { Button } from "@/components/ui/button";
import type { EventPattern } from "@/db/schema/event";
import { type HiiveEventsData, useHiiveEvents } from "@/hooks/use-hiive-data";
import { ASPECT_RATIO_CLASS } from "@/lib/aspect-ratios";

interface Props {
  hiiveId: number;
  hiiveName: string;
  hiiveSlug: string;
  initialEvents?: HiiveEventsData;
}

export function HiiveEventsTabClient({
  hiiveId,
  // Each event now carries its own host (a partner's page lists events other
  // Hiives own), so these are kept on the props only for callers.
  hiiveName: _hiiveName,
  hiiveSlug: _hiiveSlug,
  initialEvents,
}: Props) {
  const { data, error, fetchStatus, isLoading, refetch } = useHiiveEvents(
    hiiveId,
    initialEvents,
  );

  if (isLoading) {
    if (fetchStatus === "paused") {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-center text-text-secondary">
          <p>Events are waiting for network access.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        <div className="space-y-4">
          <div className="flex items-center">
            <div className="h-px flex-1 bg-linear-to-r from-transparent via-border to-border"></div>
            <div className="mx-4 h-6 w-32 animate-pulse rounded bg-surface-alt" />
            <div className="h-px flex-1 bg-linear-to-l from-transparent via-border to-border"></div>
          </div>
          <div className="grid grid-cols-3 gap-2 md:grid-cols-4 xl:grid-cols-5">
            {["skeleton-0", "skeleton-1", "skeleton-2", "skeleton-3"].map(
              (key) => (
                <div
                  key={key}
                  className={`${ASPECT_RATIO_CLASS.portrait4By5} animate-pulse rounded-2xl bg-surface-alt`}
                />
              ),
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-center text-text-secondary">
        <p>Could not load events at this time.</p>
        <Button variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const upcomingEvents = data?.upcoming || [];
  const pastEvents = data?.past || [];

  if (upcomingEvents.length === 0 && pastEvents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center text-text-secondary">
        <p>No events found for this hiive.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {upcomingEvents.length > 0 && (
        <section>
          <div className="mb-4 flex items-center">
            <div className="h-px flex-1 bg-linear-to-r from-transparent via-border to-border"></div>
            <h2 className="mx-4 text-xl font-semibold text-text-primary">
              Upcoming Events
            </h2>
            <div className="h-px flex-1 bg-linear-to-l from-transparent via-border to-border"></div>
          </div>

          <div className="relative">
            <ul
              className="grid grid-cols-3 gap-2 md:grid-cols-4 xl:grid-cols-5"
              aria-label="Upcoming events"
            >
              {upcomingEvents.map((event) => (
                <li
                  key={event.occurrenceId}
                  className="row-span-5 grid min-w-0 grid-rows-subgrid"
                >
                  <EventCard
                    alignRows
                    eventId={event.eventId}
                    occurrenceId={event.occurrenceId}
                    name={event.name}
                    slug={event.slug}
                    pattern={event.pattern as EventPattern}
                    bannerMedia={event.bannerMedia}
                    bannerMusic={event.bannerMusic ?? null}
                    venueName={event.venueName}
                    address={event.address}
                    description={event.description}
                    startTime={event.startTime}
                    endTime={event.endTime}
                    scheduleContext={event.scheduleContext}
                    // A partner's page links to the host's event; the card
                    // credits both.
                    hiiveName={event.hostHiiveName}
                    hiiveSlug={event.hostHiiveSlug}
                    collaborators={event.collaborators}
                    lowestPriceMinor={event.lowestPriceMinor}
                    currency={event.currency}
                    isDeposit={event.isDeposit}
                    totalPriceMinor={event.totalPriceMinor}
                    settlementPriceMinor={event.settlementPriceMinor}
                    settlementTotalPriceMinor={event.settlementTotalPriceMinor}
                    settlementCurrency={event.settlementCurrency}
                    priceVaries={event.priceVaries}
                    activity={event.activity}
                    hiiveLogoImage={event.hiiveLogoImage}
                    availability={event.availability}
                    bookingClosedBadge={event.bookingClosedBadge}
                    openEndedTime={event.openEndedTime}
                  />
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {pastEvents.length > 0 && (
        <section>
          <div className="mb-4 flex items-center">
            <div className="h-px flex-1 bg-linear-to-r from-transparent via-border to-border"></div>
            <h2 className="mx-4 text-xl font-semibold text-text-primary">
              Past Events
            </h2>
            <div className="h-px flex-1 bg-linear-to-l from-transparent via-border to-border"></div>
          </div>

          <div className="relative">
            <ul
              className="grid grid-cols-3 gap-2 md:grid-cols-4 xl:grid-cols-5"
              aria-label="Past events"
            >
              {pastEvents.map((event) => (
                <li
                  key={event.occurrenceId}
                  className="row-span-5 grid min-w-0 grid-rows-subgrid"
                >
                  <EventCard
                    alignRows
                    eventId={event.eventId}
                    occurrenceId={event.occurrenceId}
                    name={event.name}
                    slug={event.slug}
                    pattern={event.pattern as EventPattern}
                    bannerMedia={event.bannerMedia}
                    bannerMusic={event.bannerMusic ?? null}
                    venueName={event.venueName}
                    address={event.address}
                    description={event.description}
                    startTime={event.startTime}
                    endTime={event.endTime}
                    // A partner's page links to the host's event; the card
                    // credits both.
                    hiiveName={event.hostHiiveName}
                    hiiveSlug={event.hostHiiveSlug}
                    collaborators={event.collaborators}
                    lowestPriceMinor={event.lowestPriceMinor}
                    currency={event.currency}
                    isDeposit={event.isDeposit}
                    totalPriceMinor={event.totalPriceMinor}
                    settlementPriceMinor={event.settlementPriceMinor}
                    settlementTotalPriceMinor={event.settlementTotalPriceMinor}
                    settlementCurrency={event.settlementCurrency}
                    priceVaries={event.priceVaries}
                    activity={event.activity}
                    hiiveLogoImage={event.hiiveLogoImage}
                    openEndedTime={event.openEndedTime}
                  />
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}
